"use client";

import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Package, Heart, Users, TrendingUp, Shapes } from "lucide-react";
import { SectionContainer } from "@/components/ui/container";
import { SectionHeading } from "@/components/ui/section-heading";
import { StatsCard, AnimatedCounter } from "@/components/ui/stats-card";
import { TextGradient } from "@/components/ui/text-gradient";
import { cn } from "@/lib/utils";

export function ImpactSection() {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const progressVariants = {
    hidden: { width: 0 },
    visible: (custom: number) => ({
      width: `${custom}%`,
      transition: { duration: 1.2, ease: "easeOut" }
    })
  };

  return (
    <SectionContainer id="impact" className="bg-gray-50 relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5 pattern-bg"></div>
      
      <SectionHeading
        title={
          <>
            Our <TextGradient text="Impact" /> in Numbers
          </>
        }
        subtitle="Through our dedicated advocacy and programs, Blue Legos Matter has made significant strides in changing how blue Lego bricks are perceived and utilized."
      />

      <div ref={ref} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
        {[
          {
            icon: <Package className="h-6 w-6" />,
            value: 1250000,
            label: "Blue Bricks Rescued",
          },
          {
            icon: <Users className="h-6 w-6" />,
            value: 15000,
            label: "Active Supporters",
          },
          {
            icon: <Shapes className="h-6 w-6" />,
            value: 87,
            label: "Showcase Projects",
          },
          {
            icon: <TrendingUp className="h-6 w-6" />,
            value: 42,
            label: "% Usage Increase",
            isPercentage: true,
          },
        ].map((stat, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6, delay: i * 0.1 }}
          >
            <StatsCard
              value={inView 
                ? (stat.isPercentage 
                  ? `+${stat.value}%` 
                  : stat.value.toString())
                : (stat.isPercentage ? "0%" : "0")}
              label={stat.label}
              icon={stat.icon}
              className="h-full"
            />
          </motion.div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-md p-6 md:p-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div>
            <h3 className="text-2xl md:text-3xl font-heading font-bold mb-6">
              Increasing Blue Brick Representation
            </h3>
            <p className="text-muted-foreground mb-8">
              Our research shows that blue Lego bricks have historically been underrepresented in typical builds, accounting for only 8% of visible pieces in popular sets from 2010. Through our advocacy work, we've helped increase that number to over 18% in 2023.
            </p>

            <div className="space-y-6">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium">Average blue brick usage (2010)</span>
                  <span className="text-blue-medium font-semibold">8%</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-blue-200 rounded-full"
                    variants={progressVariants}
                    initial="hidden"
                    animate={inView ? "visible" : "hidden"}
                    custom={8}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium">Average blue brick usage (2023)</span>
                  <span className="text-blue-medium font-semibold">18%</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-blue-500 to-blue-400 rounded-full"
                    variants={progressVariants}
                    initial="hidden"
                    animate={inView ? "visible" : "hidden"}
                    custom={18}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium">Blue brick satisfaction rating</span>
                  <span className="text-blue-medium font-semibold">94%</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-blue-700 to-blue-500 rounded-full"
                    variants={progressVariants}
                    initial="hidden"
                    animate={inView ? "visible" : "hidden"}
                    custom={94}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="relative h-full flex items-center justify-center">
              <motion.div
                className="absolute w-64 h-64 bg-blue-100 rounded-full opacity-30"
                initial={{ scale: 0 }}
                animate={inView ? { scale: 1 } : { scale: 0 }}
                transition={{ duration: 1, ease: "easeOut" }}
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                animate={inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.5 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="relative z-10"
              >
                <img
                  src="https://images.unsplash.com/photo-1517242810446-cc8951b2be40?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80"
                  alt="Blue Lego sculptures showcasing their versatility"
                  className="rounded-lg shadow-2xl max-w-full object-cover aspect-square"
                />
                <div 
                  className={cn(
                    "absolute -bottom-6 -right-6 bg-white p-4 rounded-lg shadow-lg",
                    "border-l-4 border-blue-medium max-w-[200px]"
                  )}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="h-8 w-8 rounded-full bg-blue-light flex items-center justify-center">
                      <Heart className="h-4 w-4 text-blue-medium" />
                    </div>
                    <span className="font-medium text-sm">Testimonial</span>
                  </div>
                  <p className="text-sm">
                    "The blues are what make my builds stand out. They add depth, calm, and sophistication."
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">— Master Builder, Alex C.</p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </SectionContainer>
  );
}